<script>
import { mapState } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DateTime } from 'luxon';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import ServiceTimeModel from '@models/ServiceTime';
import CarUseModel from '@models/CarUse';
import ReportModel from '@models/Report';
import flatPickr from 'vue-flatpickr-component';
import CarAutoSuggest from '@components/CarAutoSuggest';
import map from 'lodash/map';
import filter from 'lodash/filter';
import HeaderComponent from '@components/HeaderComponent';
import utility from '@mixins/utility';

export default {
  page() {
    return {
      title: this.$t('titles.car_times'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    flatPickr,
    CarAutoSuggest,
    HeaderComponent,
  },
  mixins: [utility],
  data() {
    return {
      selectedCar: null,
      sortBy: 'id',
      sortDesc: true,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fields: [
        { key: 'selected', sortable: false, label: this.$t('table_labels.select') },
        {
          key: 'id',
          label: this.$t('table_labels.id'),
          sortable: true,
        },
        {
          key: 'license_plate',
          label: this.$t('table_labels.lic_plate'),
          sortable: false,
          formatter: (value, key, item) => {
            return item.car_use.car ? item.car_use.car.license_plate : '';
          },
        },
        {
          key: 'date',
          label: this.$t('fields.date'),
          sortable: true,
          formatter: (value) => {
            if (value) {
              return DateTime.fromSQL(value).toFormat('dd.MM.yyyy');
            } else {
              return '-';
            }
          },
        },        
        {
          key: 'vip',
          label: this.$t('table_labels.vip'),
          sortable: false,
          formatter: (value, key, item) => {
            return item.car_use.report.vip ? item.car_use.report.vip.name : item.car_use.report.vip_description;
          },
        },
        {
          key: 'driver',
          label: this.$t('table_labels.driver'),
          sortable: false,
          formatter: (value, key, item) => {
            return item.car_use.report.user.first_name + ' ' + item.car_use.report.user.last_name;
          },
        },
        {
          key: 'car_use',
          label: this.$t('fields.car_use'),
          sortable: true,
        },
        {
          key: 'report',
          label: this.$t('fields.report'),
          sortable: false,
        },        
        {
          key: 'completed',
          label: this.$t('fields.status'),
          sortable: false,
        },
        {
          key: 'paid',
          label: this.$t('fields.car_use_paid'),
          sortable: false,
        },
      ],
      filters: {
        licensePlate: null,
        takeOverDate: null,
        returnDate: null,
      },
      isBusy: false,
      selectedItems: [],
      selectAll: false,
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
    ...mapState({
      dateConfig: (state) => state.common.dateConfig,
    }),
  },
  watch: {
    filters: {
      handler() {
        this.$nextTick(() => {
          this.$refs.asyncTable.refresh();
        });
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    selectAllCarTimes() {
      if (this.selectAll) {
        this.selectedItems = map(this.serviceTimes, (item) => {
          if (item.car_use.car) {
            return { license_plate: item.car_use.car.license_plate, id: item.id };
          }
        });
      } else {
        this.selectedItems = [];
      }
    },
    selectedCarUsesMarkPaid() {
      this.$wait.start('service times loading');
      let selectedIds = map(this.selectedItems, 'id');

      let ids = filter(
        map(this.serviceTimes, (item) => {
          if (selectedIds.includes(item.id)) {
            return item.car_use.id;
          } else {
            return null;
          }
        }),
        (i) => i !== null
      );

      this.$http
        .put(`admin/mark-paid-service-times`, {
          ids: ids,
        })
        .then(() => {
          this.$refs.asyncTable.refresh();
        })
        .catch(() => this.$wait.end('service times loading'));
    },
    itemsProvider(ctx) {
      return ServiceTimeModel.orderBy(this.sortDir + ctx.sortBy)
        .where('takeover_date', this.filters.takeOverDate)
        .where('return_date', this.filters.returnDate)
        .where('car_use.car.license_plate', this.filters.licensePlate)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          this.serviceTimes = items;
          return items || [];
        })
        .catch(() => []);
    },
    carUseSelectDate(carUse) {
      let date = '';
      if (carUse.takeover_date) {
        date = `${DateTime.fromSQL(carUse.takeover_date).toFormat('dd.MM.yyyy HH:mm')} - `;
      } else {
        date = ` - `;
      }

      if (carUse.return_date) {
        date += `${DateTime.fromSQL(carUse.return_date).toFormat('dd.MM.yyyy HH:mm')}`;
      } else {
        date += ` - `;
      }

      return date;
    },
    timeFormatter(value) {
      return value ? DateTime.fromSQL(value).toFormat('HH:mm') : ' - ';
    },
    async deleteServiceTime(item) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.service_time') }))) {
        this.$wait.start(`service-time processing`);
        const report = new ReportModel({ id: item.car_use.report_id });
        const serviceTime = new ServiceTimeModel({ id: item.id });
        await serviceTime.for(report).delete();
        this.$refs.asyncTable.refresh();
        this.$wait.end(`service-time processing`);
      }
    },
    updatePaid(item) {
      this.$wait.start(`statusLoader${item.id}`);
      const report = new ReportModel({ id: item.report_id });
      const carUse = new CarUseModel({
        id: `status/${item.id}`,
        paid: item.paid,
      });

      carUse
        .for(report)
        .save()
        .then(() => {
          this.$wait.end(`statusLoader${item.id}`);
          this.$refs.asyncTable.refresh();
        });
    },
    onCarSelected(item) {
      this.filters.licensePlate = item.license_plate;
    },
    clearLicensePlate() {
      this.selectedCar = null;
      this.filters.licensePlate = '';
    },
    checkIsSameLicensePlateClicked(selItem) {
      if (this.selectedItems.length > 0) {
        return this.selectedItems[0].license_plate === selItem.license_plate;
      }
      return true;
    },
    clearSelected() {
      this.selectedItems = [];
      this.selectAll = false;
    },
    exportServiceTimesPdf() {
      this.$wait.start(`downloading-service-times-pdf`);
      const ids = map(this.selectedItems, 'id');
      this.$http
        .get(`admin/reports/export-service-times`, { params: { ids: ids }, responseType: 'blob' })
        .then((response) => {
          this.$wait.end(`downloading-service-times-pdf`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const fileName = this.getFileNameFromContentDisposition(String(headers['content-disposition']));
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName !== '' ? fileName : 'Car Times.pdf';
          link.click();
        })
        .catch(() => this.$wait.end(`downloading-service-times-pdf`));
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="$t('titles.car_times')" button-text="h-only"></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4 flex-column-reverse flex-sm-row">
      <b-col sm="12" md="4" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.license_plate') }}</span>
          </template>
        </HeaderComponent>
        <CarAutoSuggest
          v-model="selectedCar"
          :show-clear="true"
          :placeholder="$t('placeholders.search_license_plate')"
          class-name="form-control-sm"
          @car-selected="onCarSelected"
          @on-clear="clearLicensePlate"
        >
        </CarAutoSuggest>
      </b-col>
      <b-col sm="12" md="4" lg="4" class="mb-4 mb-md-0">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('placeholders.date_interval_start') }}</span>
          </template>
        </HeaderComponent>
        <b-input-group class="mt-0">
          <flat-pickr v-model="filters.takeOverDate" :config="dateConfig" class="form-control-sm" placeholder="Select date"></flat-pickr>
          <b-input-group-append>
            <b-btn
              variant="secondary"
              size="sm"
              class="text-white border-gray"
              :disabled="filters.takeOverDate == '' || filters.takeOverDate == null"
              @click.stop="filters.takeOverDate = ''"
              >{{ $t('actions.clear') }}</b-btn
            >
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col sm="12" md="4" lg="4" class="mb-4 mb-md-0">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('placeholders.date_interval_end') }}</span>
          </template>
        </HeaderComponent>
        <b-input-group class="mt-0">
          <flat-pickr v-model="filters.returnDate" :config="dateConfig" class="form-control-sm" placeholder="Select date"></flat-pickr>
          <b-input-group-append>
            <b-btn
              variant="secondary"
              size="sm"
              class="text-white border-gray"
              :disabled="filters.returnDate == '' || filters.returnDate == null"
              @click.stop="filters.returnDate = ''"
              >{{ $t('actions.clear') }}</b-btn
            >
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" lg="4" md="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('titles.selection_options') }}</span>
          </template>
        </HeaderComponent>
        <b-button-group class="w-100">
          <b-button size="sm" :disabled="selectedItems.length === 0" variant="outline-secondary border-gray" @click="exportServiceTimesPdf">{{
            $t('titles.export_multi_expenses_car_uses')
          }}</b-button>
          <b-button size="sm" :disabled="selectedItems.length === 0" variant="outline-secondary border-gray" @click="selectedCarUsesMarkPaid">{{
            $t('actions.mark_car_use_paid')
          }}</b-button>
        </b-button-group>
      </b-col>
    </b-row>
    <div id="service-times-index" class="position-relative table-responsive">
      <BlockingContainer v-if="$wait.is('service-time processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        :items="itemsProvider"
        stacked="lg"
        striped
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fields"
        :no-sort-reset="true"
        :show-empty="true"
        table-class="no-min-width"
        small
        responsive
      >
        <template #head(selected)="data">
          <span>{{ data.label }}</span>
          <span><b-form-checkbox id="checkboxSelectAll" v-model="selectAll" @change="selectAllCarTimes"> </b-form-checkbox></span>
        </template>
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(selected)="data">
          <b-form-checkbox
            v-if="data.item.car_use.car"
            :id="'checkbox' + data.index"
            v-model="selectedItems"
            :value="{ license_plate: data.item.car_use.car.license_plate, id: data.item.id }"
          >
          </b-form-checkbox>
          <!-- Doing it with two checkboxes because disable won't work on above element, because value still tries
                    to take license_plate of non existing car -->
          <b-form-checkbox v-else disabled>&nbsp;</b-form-checkbox>
        </template>
        <template v-slot:cell(vip)="data">
          {{ data.value }}
        </template>
        <template v-slot:cell(car_use)="row">
          <div v-if="row.item.car_use.car"> #{{ row.item.car_use.id }} - {{ row.item.car_use.car.name }} ({{ carUseSelectDate(row.item.car_use) }}) </div>
          <div v-else> - </div>
        </template>
        <template v-slot:cell(report)="row">
          <div v-if="row.item.car_use.report_id"><router-link :to="{ name: 'reports.edit', params: { id: row.item.car_use.report_id } }" target="_blank">#{{ row.item.car_use.report_id }}</router-link></div>
        </template>        
        <template v-slot:cell(date)="data">
          <div>{{ data.value }}</div>
        </template>
        <template v-slot:cell(completed)="row">
          <FontAwesomeIcon v-if="row.item.completed" class="text-success" :icon="['fas', 'check-circle']" />
          <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
        </template>
        <template v-slot:cell(paid)="row">
          <div v-if="row.item.car_use" v-can:superAdministrate :class="$style.statusContainer">
            <div v-show="!$wait.is(`statusLoader${row.item.car_use.id}`)">
              <b-form-checkbox v-model="row.item.car_use.paid" :value="1" :unchecked-value="0" @change="updatePaid(row.item.car_use)">&nbsp;</b-form-checkbox>
            </div>
            <FontAwesomeIcon v-show="$wait.is(`statusLoader${row.item.car_use.id}`)" :class="$style.statuLoadingIcon" :icon="['fas', 'spinner']" pulse />
          </div>
          <div v-if="row.item.car_use" v-can:superAdministrate.not>
            <FontAwesomeIcon v-if="parseInt(row.item.car_use.paid, 10)" class="text-success" :icon="['fas', 'check-circle']" />
            <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
          </div>
        </template>
        <template v-slot:row-details="row">
          <b-card>
            {{ row.item.notes }}
          </b-card>
        </template>
      </b-table>
    </div>
    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
  </Layout>
</template>
<style lang="scss" module>
table {
  font-size: 0.7rem;
}

.statusContainer {
  padding-left: 1rem;
  margin: 0;
}

.statusLoadingIcon {
  margin: 0 auto;
}
</style>
